import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminUserListResponseDto,
  AdminUserResponseDto,
  MergePreviewResponseDto,
  UserActivityResponseDto,
  UserRoleResponseDto,
  UserSourcesResponseDto,
} from '@lunary/api-client'
import { api } from '../../shared/api/client'
import type { Role } from './labels'

export const ADMIN_USERS_KEY = ['admin-users'] as const

const userPath = (id: string): string => `/admin/users/${encodeURIComponent(id)}`

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'one', id],
    queryFn: () => api.get<AdminUserResponseDto>(userPath(id)),
    retry: false,
  })
}

export function useUserActivity(id: string) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'activity', id],
    queryFn: () => api.get<UserActivityResponseDto>(`${userPath(id)}/activity`),
    retry: false,
  })
}

export function useUserSources(id: string) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'sources', id],
    queryFn: () => api.get<UserSourcesResponseDto>(`${userPath(id)}/sources`),
    retry: false,
  })
}

export function useSetRole(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (role: Role) => api.patch<UserRoleResponseDto>(`/users/${encodeURIComponent(id)}/role`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}

export function useUserActions(id: string) {
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
  return {
    block: useMutation({
      mutationFn: (reason: string) => api.post<AdminUserResponseDto>(`${userPath(id)}/block`, { reason: reason || undefined }),
      onSuccess,
    }),
    unblock: useMutation({
      mutationFn: () => api.post<AdminUserResponseDto>(`${userPath(id)}/unblock`),
      onSuccess,
    }),
    revokeSessions: useMutation({
      mutationFn: () => api.post<undefined>(`${userPath(id)}/revoke-sessions`),
      onSuccess,
    }),
  }
}

export function useUserSearch(q: string, enabled: boolean) {
  const query = q.trim()
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'search', query],
    queryFn: () => api.get<AdminUserListResponseDto>('/admin/users', { query: { q: query, limit: 8, offset: 0 } }),
    enabled: enabled && query.length >= 2,
    retry: false,
  })
}

export function useMergePreview(id: string, sourceId: string | null) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'merge-preview', id, sourceId],
    queryFn: () =>
      api.get<MergePreviewResponseDto>(`${userPath(id)}/merge-preview`, { query: { source_id: sourceId ?? '' } }),
    enabled: sourceId !== null,
    retry: false,
  })
}

export function useMergeUsers(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sourceId: string) => api.post<AdminUserResponseDto>(`${userPath(id)}/merge`, { source_id: sourceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  })
}
